import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '@/components/mypage/PageHeader';
import { useToast } from '@/components/common/Toast';
import { useLoadingCoordinator, SkeletonComponents } from '@/hooks/useLoadingCoordinator';
import { termsPolicyService } from '@/services/termsPolicy';
import type { TermsPolicy } from '@/types';

export function TermsPolicyDetailPage() {
  const { id, title } = useParams<{ id?: string; title?: string }>();
  const { showToast, ToastContainer } = useToast();
  const [termsPolicy, setTermsPolicy] = useState<TermsPolicy | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchTermsPolicy = async () => {
      if (!id && !title) {
        showToast('잘못된 접근입니다');
        return;
      }

      try {
        setIsDataLoading(true);
        let result;
        
        if (title) {
          // 제목으로 조회 (URL 디코딩 필요)
          const decodedTitle = decodeURIComponent(title);
          result = await termsPolicyService.getTermsPolicyByTitle(decodedTitle);
        } else if (id) {
          // ID로 조회 (기존 방식)
          result = await termsPolicyService.getTermsPolicyById(id);
        }
        
        if (result?.success && result.data) {
          setTermsPolicy(result.data);
        } else {
          showToast(result?.error || '약관 및 정책을 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Error fetching terms policy:', error);
        showToast('약관 및 정책을 불러오는데 실패했습니다');
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchTermsPolicy();
  }, [id, title, showToast]);

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="약관 및 정책" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">문서를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="약관 및 정책" />
        <SkeletonComponents.DetailPage />
      </div>
    );
  }

  // 콘텐츠 표시
  if (shouldShowContent) {
    if (!termsPolicy) {
      return (
        <div className="min-h-screen bg-white">
          <PageHeader title="약관 및 정책" />
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 12L12 8L8 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="text-gray-600 mb-2">문서를 찾을 수 없습니다</p>
              <p className="text-gray-500 text-sm">요청하신 문서가 존재하지 않거나 삭제되었습니다</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-white">
        <PageHeader title="약관 및 정책" />
        
        <div className="px-4 py-6">
          {/* 문서 헤더 */}
          <div className="mb-6 pb-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-[#222222] mb-2">
              {termsPolicy.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>
                {new Date(termsPolicy.updated_at).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })} 업데이트
              </span>
              {termsPolicy.version && (
                <span>버전 {termsPolicy.version}</span>
              )}
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                {termsPolicy.category === 'terms' ? '이용약관' : '개인정보처리방침'}
              </span>
            </div>
          </div>

          {/* 문서 내용 */}
          <div className="prose prose-sm max-w-none">
            <div
              className="text-gray-700 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: termsPolicy.content }}
            />
          </div>
        </div>
        
        <ToastContainer />
      </div>
    );
  }

  // 폴백 (예상되지 않는 상태)
  return null;
}